import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import { fetchRules, createRule, updateRule, deleteRule } from '../lib/api';
import { safeArray } from '../lib/safe';
import type { ProductRule, RuleCondition } from '../types';

export const Route = createFileRoute('/rules')({
  component: RulesPage,
});

// ── Quick-create templates ──────────────────────────────
const TEMPLATES = [
  {
    label: 'Brand from Vendor',
    rule_type: 'vendor_brand' as const,
    conditions: { all: [{ field: 'vendor', op: 'equals', value: '' }] },
    actions: { set_brand: '' },
  },
  {
    label: 'Collection Tag Rule',
    rule_type: 'collection' as const,
    conditions: { all: [{ field: 'tags', op: 'contains', value: '' }] },
    actions: { set_collection: '' },
  },
  {
    label: 'Product Type Override',
    rule_type: 'product_type' as const,
    conditions: { all: [{ field: 'product_type', op: 'equals', value: '' }] },
    actions: { set_product_type: '' },
  },
];

const EMPTY_RULE = {
  name: '',
  rule_type: 'vendor_brand',
  priority: 100,
  conditions: { all: [{ field: 'vendor', op: 'equals', value: '' }] },
  actions: { set_brand: '' } as Record<string, any>,
};

const RULE_TYPES = ['vendor_brand', 'product_type', 'collection', 'custom'] as const;
const SOURCES = ['manual', 'auto_suggested'] as const;
const CONDITION_FIELDS = ['vendor', 'title', 'tags', 'product_type', 'collection'] as const;
const CONDITION_OPS = ['equals', 'contains', 'starts_with', 'regex'] as const;

// ── Helper: type badge color ────────────────────────────
function typeBadgeColor(type: string): string {
  switch (type) {
    case 'vendor_brand': return 'bg-purple-50 text-purple-700';
    case 'product_type': return 'bg-teal-50 text-teal-700';
    case 'collection': return 'bg-indigo-50 text-indigo-700';
    case 'custom': return 'bg-slate-100 text-slate-700';
    default: return 'bg-slate-50 text-slate-600';
  }
}

function sourceBadgeColor(source: string): string {
  return source === 'auto_suggested'
    ? 'bg-amber-50 text-amber-700'
    : 'bg-blue-50 text-blue-700';
}

// ── Main component ──────────────────────────────────────
function RulesPage() {
  const queryClient = useQueryClient();

  // ── Data fetching ─────────────────────────────────────
  const { data, isLoading, error } = useQuery({
    queryKey: ['rules'],
    queryFn: () => fetchRules(),
  });

  const rules = safeArray(data?.rules);

  // ── Filter state ──────────────────────────────────────
  const [filterType, setFilterType] = useState<string>('');
  const [filterSource, setFilterSource] = useState<string>('');
  const [filterEnabled, setFilterEnabled] = useState<boolean | null>(null);
  const [searchText, setSearchText] = useState('');

  // ── Form state ────────────────────────────────────────
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({ ...EMPTY_RULE });
  const [formConditions, setFormConditions] = useState<RuleCondition[]>([{ field: 'vendor', op: 'equals', value: '' }]);
  const [formActions, setFormActions] = useState<Record<string, any>>({ set_brand: '' });
  const [formSkipWhiteLabel, setFormSkipWhiteLabel] = useState(false);

  // ── Delete confirmation ───────────────────────────────
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // ── Mutations ─────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: (body: Partial<ProductRule>) => createRule(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rules'] });
      closeForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<ProductRule> }) => updateRule(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rules'] });
      closeForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rules'] });
      setDeleteConfirmId(null);
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: number }) => updateRule(id, { enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rules'] }),
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => updateRule(id, { enabled: 1, source: 'manual' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rules'] }),
  });

  // ── Filtered rules ────────────────────────────────────
  const filteredRules = useMemo(() => {
    return rules.filter((r) => {
      if (filterType && r.rule_type !== filterType) return false;
      if (filterSource && r.source !== filterSource) return false;
      if (filterEnabled !== null && Boolean(r.enabled) !== filterEnabled) return false;
      if (searchText && !r.name.toLowerCase().includes(searchText.toLowerCase())) return false;
      return true;
    });
  }, [rules, filterType, filterSource, filterEnabled, searchText]);

  // ── Form helpers ──────────────────────────────────────
  function openCreateForm(template?: typeof TEMPLATES[number]) {
    if (template) {
      setFormData({
        name: '',
        rule_type: template.rule_type,
        priority: 100,
        conditions: template.conditions,
        actions: template.actions,
      });
      setFormConditions([...template.conditions.all]);
      setFormActions({ ...template.actions });
    } else {
      setFormData({ ...EMPTY_RULE });
      setFormConditions([{ field: 'vendor', op: 'equals', value: '' }]);
      setFormActions({ set_brand: '' });
    }
    setFormSkipWhiteLabel(false);
    setEditingId(null);
    setShowForm(true);
  }

  function openEditForm(rule: ProductRule) {
    setFormData({
      name: rule.name,
      rule_type: rule.rule_type,
      priority: rule.priority,
      conditions: rule.conditions,
      actions: rule.actions,
    });
    const conditions = rule.conditions?.all ?? [{ field: 'vendor', op: 'equals', value: '' }];
    setFormConditions([...conditions]);
    setFormActions({ ...rule.actions });
    setFormSkipWhiteLabel(Boolean(rule.actions?.skip_white_label));
    setEditingId(rule.id);
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditingId(null);
    setFormData({ ...EMPTY_RULE });
    setFormConditions([{ field: 'vendor', op: 'equals', value: '' }]);
    setFormActions({ set_brand: '' });
    setFormSkipWhiteLabel(false);
  }

  function handleSave() {
    const actions = { ...formActions };
    if (formSkipWhiteLabel) {
      actions.skip_white_label = true;
    } else {
      delete actions.skip_white_label;
    }
    // Clean empty action values
    for (const key of Object.keys(actions)) {
      if (actions[key] === '' && key !== 'skip_white_label') {
        delete actions[key];
      }
    }

    const body: Partial<ProductRule> = {
      name: formData.name,
      rule_type: formData.rule_type,
      priority: formData.priority,
      conditions: { all: formConditions },
      actions,
    };

    if (editingId) {
      updateMutation.mutate({ id: editingId, body });
    } else {
      createMutation.mutate(body);
    }
  }

  function updateCondition(index: number, field: keyof RuleCondition, value: string) {
    setFormConditions((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  }

  function addCondition() {
    setFormConditions((prev) => [...prev, { field: 'vendor', op: 'equals', value: '' }]);
  }

  function removeCondition(index: number) {
    setFormConditions((prev) => prev.filter((_, i) => i !== index));
  }

  // ── Render ────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-[var(--ccb-text)]">Product Rules</h2>
        <div className="flex items-center gap-3">
          {/* Quick-create templates */}
          <div className="relative group">
            <button className="text-sm font-medium px-3 py-1.5 rounded-lg border border-[var(--ccb-border)] text-[var(--ccb-text-secondary)] hover:bg-slate-50 transition-colors">
              Templates
              <svg className="inline-block ml-1 w-3 h-3" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 5l3 3 3-3"/></svg>
            </button>
            <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-lg border border-[var(--ccb-border)] shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-20">
              {TEMPLATES.map((t) => (
                <button
                  key={t.label}
                  onClick={() => openCreateForm(t)}
                  className="block w-full text-left px-4 py-2 text-sm text-[var(--ccb-text)] hover:bg-slate-50 first:rounded-t-lg last:rounded-b-lg"
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={() => openCreateForm()}
            className="text-sm font-medium px-4 py-1.5 rounded-lg bg-[var(--ccb-primary)] text-white hover:opacity-90 transition-opacity"
          >
            Create Rule
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 bg-white rounded-xl border border-[var(--ccb-border)] px-4 py-3">
        {/* Type filter */}
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="text-sm border border-[var(--ccb-border)] rounded-lg px-3 py-1.5 text-[var(--ccb-text)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--ccb-primary)]/30"
        >
          <option value="">All Types</option>
          {RULE_TYPES.map((t) => (
            <option key={t} value={t}>{t.replace('_', ' ')}</option>
          ))}
        </select>

        {/* Source filter */}
        <select
          value={filterSource}
          onChange={(e) => setFilterSource(e.target.value)}
          className="text-sm border border-[var(--ccb-border)] rounded-lg px-3 py-1.5 text-[var(--ccb-text)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--ccb-primary)]/30"
        >
          <option value="">All Sources</option>
          {SOURCES.map((s) => (
            <option key={s} value={s}>{s.replace('_', ' ')}</option>
          ))}
        </select>

        {/* Enabled toggle */}
        <button
          onClick={() => setFilterEnabled(filterEnabled === null ? true : filterEnabled === true ? false : null)}
          className={`text-sm px-3 py-1.5 rounded-lg border transition-colors ${
            filterEnabled === null
              ? 'border-[var(--ccb-border)] text-[var(--ccb-text-secondary)]'
              : filterEnabled
                ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                : 'border-red-300 bg-red-50 text-red-700'
          }`}
        >
          {filterEnabled === null ? 'All' : filterEnabled ? 'Enabled' : 'Disabled'}
        </button>

        {/* Search */}
        <div className="flex-1 min-w-[180px]">
          <input
            type="text"
            placeholder="Search rule name..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="w-full text-sm border border-[var(--ccb-border)] rounded-lg px-3 py-1.5 text-[var(--ccb-text)] placeholder:text-[var(--ccb-text-secondary)]/50 focus:outline-none focus:ring-2 focus:ring-[var(--ccb-primary)]/30"
          />
        </div>

        {/* Result count */}
        <span className="text-xs text-[var(--ccb-text-secondary)]">
          {filteredRules.length} rule{filteredRules.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Auto-suggested banner */}
      {filterSource === 'auto_suggested' && filteredRules.some((r) => r.source === 'auto_suggested') && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <p className="text-sm font-medium text-amber-800">
            AI-Suggested Rules
          </p>
          <p className="text-xs text-amber-600 mt-0.5">
            These rules were auto-generated based on brand detection patterns. Review and approve or reject each one.
          </p>
        </div>
      )}

      {/* Create/Edit Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-[var(--ccb-border)] overflow-hidden">
          <div className="px-4 py-3 border-b border-[var(--ccb-border)] bg-slate-50/50">
            <h3 className="text-sm font-semibold text-[var(--ccb-text)]">
              {editingId ? 'Edit Rule' : 'Create Rule'}
            </h3>
          </div>
          <div className="p-4 space-y-4">
            {/* Top row: Name, Type, Priority */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-[var(--ccb-text-secondary)] mb-1">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Judy Blue Brand Rule"
                  className="w-full text-sm border border-[var(--ccb-border)] rounded-lg px-3 py-1.5 text-[var(--ccb-text)] focus:outline-none focus:ring-2 focus:ring-[var(--ccb-primary)]/30"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--ccb-text-secondary)] mb-1">Rule Type</label>
                <select
                  value={formData.rule_type}
                  onChange={(e) => setFormData((prev) => ({ ...prev, rule_type: e.target.value }))}
                  className="w-full text-sm border border-[var(--ccb-border)] rounded-lg px-3 py-1.5 text-[var(--ccb-text)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--ccb-primary)]/30"
                >
                  {RULE_TYPES.map((t) => (
                    <option key={t} value={t}>{t.replace('_', ' ')}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--ccb-text-secondary)] mb-1">Priority</label>
                <input
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData((prev) => ({ ...prev, priority: parseInt(e.target.value) || 100 }))}
                  className="w-full text-sm border border-[var(--ccb-border)] rounded-lg px-3 py-1.5 text-[var(--ccb-text)] focus:outline-none focus:ring-2 focus:ring-[var(--ccb-primary)]/30"
                />
              </div>
            </div>

            {/* Visual Condition Builder */}
            <div>
              <label className="block text-xs font-medium text-[var(--ccb-text-secondary)] mb-2">Conditions</label>
              <div className="space-y-2">
                {formConditions.map((cond, i) => (
                  <div key={i} className="flex items-center gap-2">
                    {i > 0 && (
                      <span className="text-[10px] font-semibold text-[var(--ccb-text-secondary)] uppercase w-8 text-center shrink-0">AND</span>
                    )}
                    {i === 0 && <span className="w-8 shrink-0" />}
                    <select
                      value={cond.field}
                      onChange={(e) => updateCondition(i, 'field', e.target.value)}
                      className="text-sm border border-[var(--ccb-border)] rounded-lg px-2 py-1.5 text-[var(--ccb-text)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--ccb-primary)]/30"
                    >
                      {CONDITION_FIELDS.map((f) => (
                        <option key={f} value={f}>{f.replace('_', ' ')}</option>
                      ))}
                    </select>
                    <select
                      value={cond.op}
                      onChange={(e) => updateCondition(i, 'op', e.target.value)}
                      className="text-sm border border-[var(--ccb-border)] rounded-lg px-2 py-1.5 text-[var(--ccb-text)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--ccb-primary)]/30"
                    >
                      {CONDITION_OPS.map((o) => (
                        <option key={o} value={o}>{o.replace('_', ' ')}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={cond.value}
                      onChange={(e) => updateCondition(i, 'value', e.target.value)}
                      placeholder="Value..."
                      className="flex-1 text-sm border border-[var(--ccb-border)] rounded-lg px-3 py-1.5 text-[var(--ccb-text)] focus:outline-none focus:ring-2 focus:ring-[var(--ccb-primary)]/30"
                    />
                    {formConditions.length > 1 && (
                      <button
                        onClick={() => removeCondition(i)}
                        className="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        title="Remove condition"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={addCondition}
                  className="text-xs font-medium text-[var(--ccb-primary)] hover:underline ml-8"
                >
                  + Add Condition
                </button>
              </div>
            </div>

            {/* Actions */}
            <div>
              <label className="block text-xs font-medium text-[var(--ccb-text-secondary)] mb-2">Actions</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-wide font-semibold text-[var(--ccb-text-secondary)] mb-1">Set Brand</label>
                  <input
                    type="text"
                    value={formActions.set_brand ?? ''}
                    onChange={(e) => setFormActions((prev) => ({ ...prev, set_brand: e.target.value }))}
                    placeholder="e.g. Judy Blue"
                    className="w-full text-sm border border-[var(--ccb-border)] rounded-lg px-3 py-1.5 text-[var(--ccb-text)] focus:outline-none focus:ring-2 focus:ring-[var(--ccb-primary)]/30"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wide font-semibold text-[var(--ccb-text-secondary)] mb-1">Set Product Type</label>
                  <input
                    type="text"
                    value={formActions.set_product_type ?? ''}
                    onChange={(e) => setFormActions((prev) => ({ ...prev, set_product_type: e.target.value }))}
                    placeholder="e.g. Jeans"
                    className="w-full text-sm border border-[var(--ccb-border)] rounded-lg px-3 py-1.5 text-[var(--ccb-text)] focus:outline-none focus:ring-2 focus:ring-[var(--ccb-primary)]/30"
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 mt-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formSkipWhiteLabel}
                  onChange={(e) => setFormSkipWhiteLabel(e.target.checked)}
                  className="w-4 h-4 rounded border-[var(--ccb-border)] text-[var(--ccb-primary)] focus:ring-[var(--ccb-primary)]/30"
                />
                <span className="text-sm text-[var(--ccb-text)]">Skip white-label processing</span>
              </label>
            </div>

            {/* Mutation error */}
            {(createMutation.error || updateMutation.error) && (
              <p className="text-sm text-red-600">
                {(createMutation.error || updateMutation.error)?.message ?? 'Failed to save rule'}
              </p>
            )}

            {/* Form buttons */}
            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={handleSave}
                disabled={createMutation.isPending || updateMutation.isPending || !formData.name.trim()}
                className="text-sm font-medium px-4 py-1.5 rounded-lg bg-[var(--ccb-primary)] text-white hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {createMutation.isPending || updateMutation.isPending ? 'Saving...' : editingId ? 'Update Rule' : 'Save Rule'}
              </button>
              <button
                onClick={closeForm}
                className="text-sm font-medium px-4 py-1.5 rounded-lg border border-[var(--ccb-border)] text-[var(--ccb-text-secondary)] hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rules table */}
      <div className="bg-white rounded-xl border border-[var(--ccb-border)] overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center text-sm text-[var(--ccb-text-secondary)]">Loading rules...</div>
        ) : error ? (
          <div className="p-12 text-center text-sm text-red-600">
            {(error as Error).message ?? 'Failed to load rules'}
          </div>
        ) : filteredRules.length === 0 ? (
          <div className="p-12 text-center text-sm text-[var(--ccb-text-secondary)]">
            {rules.length === 0 ? 'No rules yet. Create one to get started.' : 'No rules match the current filters.'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--ccb-border)]">
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--ccb-text-secondary)] uppercase tracking-wide">Name</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--ccb-text-secondary)] uppercase tracking-wide">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--ccb-text-secondary)] uppercase tracking-wide">Source</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--ccb-text-secondary)] uppercase tracking-wide">Priority</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--ccb-text-secondary)] uppercase tracking-wide">Matches</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--ccb-text-secondary)] uppercase tracking-wide">Enabled</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-[var(--ccb-text-secondary)] uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRules.map((rule) => (
                <tr key={rule.id} className="border-b border-[var(--ccb-border)] last:border-0 hover:bg-slate-50/50">
                  {/* Name */}
                  <td className="px-4 py-3">
                    <p className="font-medium text-[var(--ccb-text)] truncate max-w-[200px]">{rule.name}</p>
                    {/* Auto-suggested inline banner */}
                    {rule.source === 'auto_suggested' && (
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] text-amber-600 italic">AI suggested</span>
                        {rule.confidence != null && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 font-medium">
                            {Math.round(rule.confidence * 100)}% confidence
                          </span>
                        )}
                      </div>
                    )}
                  </td>

                  {/* Type badge */}
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${typeBadgeColor(rule.rule_type)}`}>
                      {rule.rule_type.replace('_', ' ')}
                    </span>
                  </td>

                  {/* Source badge */}
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${sourceBadgeColor(rule.source)}`}>
                      {rule.source.replace('_', ' ')}
                    </span>
                  </td>

                  {/* Priority */}
                  <td className="px-4 py-3 text-[var(--ccb-text-secondary)]">
                    {rule.priority}
                  </td>

                  {/* Match count */}
                  <td className="px-4 py-3 text-[var(--ccb-text-secondary)]">
                    {rule.match_count ?? 0}
                  </td>

                  {/* Enabled toggle */}
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleMutation.mutate({ id: rule.id, enabled: rule.enabled ? 0 : 1 })}
                      disabled={toggleMutation.isPending}
                      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50 ${
                        rule.enabled ? 'bg-emerald-500' : 'bg-slate-200'
                      }`}
                      title={rule.enabled ? 'Disable' : 'Enable'}
                    >
                      <span
                        className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out ${
                          rule.enabled ? 'translate-x-4' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {/* Auto-suggested: Approve / Reject */}
                      {rule.source === 'auto_suggested' && (
                        <>
                          <button
                            onClick={() => approveMutation.mutate(rule.id)}
                            disabled={approveMutation.isPending}
                            className="text-xs font-medium text-emerald-600 hover:underline disabled:opacity-50"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => setDeleteConfirmId(rule.id)}
                            disabled={deleteMutation.isPending}
                            className="text-xs font-medium text-red-500 hover:underline disabled:opacity-50"
                          >
                            Reject
                          </button>
                          <span className="w-px h-4 bg-[var(--ccb-border)]" />
                        </>
                      )}

                      <button
                        onClick={() => openEditForm(rule)}
                        className="text-xs font-medium text-[var(--ccb-primary)] hover:underline"
                      >
                        Edit
                      </button>

                      {deleteConfirmId === rule.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => deleteMutation.mutate(rule.id)}
                            disabled={deleteMutation.isPending}
                            className="text-xs font-medium text-red-600 hover:underline disabled:opacity-50"
                          >
                            {deleteMutation.isPending ? 'Deleting...' : 'Confirm'}
                          </button>
                          <button
                            onClick={() => setDeleteConfirmId(null)}
                            className="text-xs font-medium text-[var(--ccb-text-secondary)] hover:underline"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirmId(rule.id)}
                          className="text-xs font-medium text-red-500 hover:underline"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
